import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExpressionEditor } from "@/components/bi/ExpressionEditor";
import { ROW_FUNCTIONS, MEASURE_FUNCTIONS } from "@/lib/functionLibrary";
import { FunctionSquare, Sigma, BookOpen, Trash2 } from "lucide-react";

export function FunctionLibraryPanel({ mode, onInsert }) {
  const groups = mode === "measure" ? MEASURE_FUNCTIONS : ROW_FUNCTIONS;
  return (
    <div className="w-56 shrink-0 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] flex flex-col min-h-0 max-h-[380px]">
      <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] flex items-center gap-1 shrink-0"><BookOpen className="w-3 h-3" />Function library</div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-2 pb-2 space-y-2">
          {groups.map((g) => (
            <div key={g.category}>
              <div className="text-[10px] font-semibold text-[color:var(--ink-3)] px-1 mb-0.5">{g.category}</div>
              {g.fns.map((f) => (
                <button key={f.name} data-testid="fn-lib-item" title={f.desc}
                  onClick={() => onInsert(f.insert)}
                  className="w-full text-left px-2 py-1 rounded-md hover:bg-[color:var(--surface)] group">
                  <span className="text-[11px] font-mono text-[color:var(--brand-ocean)]">{f.sig}</span>
                  <span className="block text-[10px] text-[color:var(--ink-3)] leading-tight">{f.desc}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ---------------- Row-level calculated field (dataset column) ----------------
export function CalcFieldDialog({ open, onOpenChange, dataset, editField, onSaved }) {
  const [name, setName] = useState("");
  const [expr, setExpr] = useState("");
  const [busy, setBusy] = useState(false);
  const [exprStatus, setExprStatus] = useState(null);
  const [model, setModel] = useState(null);
  const edApi = useRef(null);
  useEffect(() => {
    if (open) { setName(editField?.name || ""); setExpr(editField?.expr || ""); setExprStatus(null); }
  }, [open, editField]);
  useEffect(() => {
    if (open && dataset?.id) {
      api.get(`/datasets/${dataset.id}/model`).then((r) => setModel(r.data)).catch(() => setModel(null));
    }
  }, [open, dataset?.id]);
  if (!dataset) return null;
  const labelFor = (col) => {
    if (!model) return null;
    const d = (model.dimensions || []).find((x) => x.field === col && x.label && x.label !== col);
    if (d) return d.label;
    const m = (model.measures || []).find((x) => x.kind !== "calculated" && x.field === col && x.label && x.label !== col);
    if (m) return m.label;
    const fm = (model.field_meta || {})[col];
    return fm && fm.label && fm.label !== col ? fm.label : null;
  };
  const suggestions = (dataset.schema || [])
    .filter((c) => !editField || c.name !== editField.name)
    .map((c) => {
      const disp = labelFor(c.name);
      return { label: disp || c.name, value: c.name, detail: disp ? `${c.type} · ${c.name}` : c.type };
    });
  const invalid = exprStatus?.state === "error" || exprStatus?.state === "checking";

  const save = async () => {
    const nm = name.trim();
    if (!nm) { toast.error("Give the field a name"); return; }
    if (!expr.trim()) { toast.error("Write an expression"); return; }
    setBusy(true);
    const others = (dataset.calc_fields || []).filter((f) => f.name !== (editField?.name || nm) && f.name !== nm);
    try {
      const { data } = await api.put(`/datasets/${dataset.id}/calc-fields`, { fields: [...others, { name: nm, expr: expr.trim() }] });
      toast.success(`Calculated field "${nm}" saved`);
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editField) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/datasets/${dataset.id}/calc-fields`, { fields: (dataset.calc_fields || []).filter((f) => f.name !== editField.name) });
      toast.success("Calculated field deleted");
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="calc-field-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FunctionSquare className="w-5 h-5 text-[color:var(--brand-mint)]" />{editField ? "Edit calculated field" : "New calculated field"}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Row-level column computed from other columns in <b>{dataset.name}</b>. Reference fields as <span className="font-mono">[Field Name]</span>. Text results appear under <b>Dimensions</b>, numbers under <b>Measures</b>. Supports IF / IIF, multi-WHEN CASE, IN / NOT IN, EXTRACT, CAST, CURRENTYEAR / PREVIOUSYEAR and more.</p>
        <div className="flex gap-3 min-h-0">
          <div className="flex-1 min-w-0 space-y-3">
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Field name</Label>
              <Input data-testid="calc-field-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. profit_margin" className="mt-1 bg-[color:var(--surface)]" />
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Expression — type to autocomplete, or drag fields from the data pane</Label>
              <div className="mt-1">
                <ExpressionEditor value={expr} onChange={setExpr} mode="row" suggestions={suggestions} apiRef={edApi}
                  testid="calc-field-expression" onStatusChange={setExprStatus}
                  placeholder={"e.g. IF([Unit Price] - [Unit Cost] > 100, \"High\", \"Low\")\nor ([sales] - [cost]) / [sales] * 100"}
                  validate={(v) => api.post(`/datasets/${dataset.id}/calc-fields/validate`, { name: name || "calc", expr: v }).then((r) => r.data)} />
              </div>
            </div>
          </div>
          <FunctionLibraryPanel mode="row" onInsert={(t) => edApi.current?.insert(t)} />
        </div>
        <div className="flex justify-between items-center pt-1">
          {editField ? <Button data-testid="calc-field-delete" variant="ghost" disabled={busy} onClick={remove} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4 mr-1" />Delete</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="calc-field-save" disabled={busy || invalid || !name.trim() || !expr.trim()} onClick={save} className="bg-[color:var(--brand-ocean)] text-white">{exprStatus?.state === "checking" ? "Validating…" : "Save field"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Calculated measure (semantic model) ----------------
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "measure";

export function MeasureEditorDialog({ open, onOpenChange, datasetId, model, editMeasure, onSaved }) {
  const [label, setLabel] = useState("");
  const [expr, setExpr] = useState("");
  const [fmt, setFmt] = useState({ type: "number", decimals: 0, compact: false, prefix: "", suffix: "", currency: "USD" });
  const [busy, setBusy] = useState(false);
  const [exprStatus, setExprStatus] = useState(null);
  const [schemaCols, setSchemaCols] = useState([]);
  const edApi = useRef(null);
  useEffect(() => {
    if (open) {
      setLabel(editMeasure?.label || "");
      setExpr(editMeasure?.expression || "");
      setExprStatus(null);
      setFmt({ type: "number", decimals: 0, compact: false, prefix: "", suffix: "", currency: "USD", ...(editMeasure?.format || {}) });
    }
  }, [open, editMeasure]);
  // Column suggestions: the model payload carries dataset_schema; callers that strip it fall back to the dataset doc.
  useEffect(() => {
    if (!open) return;
    if (Array.isArray(model?.dataset_schema) && model.dataset_schema.length) { setSchemaCols(model.dataset_schema); return; }
    if (!datasetId) { setSchemaCols([]); return; }
    api.get(`/datasets/${datasetId}`).then((r) => setSchemaCols(r.data?.schema || [])).catch(() => setSchemaCols([]));
  }, [open, datasetId, model?.dataset_schema]);

  const basicMeasures = (model?.measures || []).filter((m) => m.visible !== false && (m.kind !== "calculated" || m.key !== editMeasure?.key));
  const columns = schemaCols.filter((c) => c && c.name);
  const suggestions = [
    ...basicMeasures.map((m) => ({ kind: "field", label: m.label || m.key, value: m.key, detail: m.kind === "calculated" ? "ƒx measure" : `${m.agg} measure` })),
    ...columns.map((c) => ({ kind: "column", label: c.name, value: c.name, detail: `${c.type || "column"} · use inside SUM/COUNTD(...)` })),
  ];

  const save = async () => {
    const lb = label.trim();
    if (!lb) { toast.error("Give the measure a label"); return; }
    if (!expr.trim()) { toast.error("Write an expression"); return; }
    setBusy(true);
    const key = editMeasure?.key || slug(lb);
    const entry = { key, label: lb, kind: "calculated", expression: expr.trim(), visible: true, format: fmt };
    const measures = editMeasure
      ? model.measures.map((m) => m.key === editMeasure.key ? { ...m, ...entry } : m)
      : [...model.measures, entry];
    try {
      const body = {
        name: model.name || "Model", description: model.description || "", time_dimension: model.time_dimension || null,
        dimensions: model.dimensions || [], measures, hierarchies: model.hierarchies || [],
        rls_rules: model.rls_rules || [], relationships: model.relationships || [], field_meta: model.field_meta || {},
      };
      const { data } = await api.put(`/datasets/${datasetId}/model`, body);
      toast.success(`Measure "${lb}" saved`);
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="measure-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sigma className="w-5 h-5 text-[color:var(--brand-ocean)]" />{editMeasure ? "Edit calculated measure" : "New calculated measure"}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Aggregated business logic. Reference governed measures as <span className="font-mono">[measure]</span>, or aggregate dataset columns directly: <span className="font-mono">COUNTD(IF([status] IN ('Billed','Paid'), [Dealer Code], NULL))</span>, <span className="font-mono">SUM(IF(EXTRACT(YEAR FROM [Invoice Date]) = CURRENTYEAR(), [amount], 0))</span>. Supports IF / IIF, multi-WHEN CASE, IN / NOT IN, CAST, EXTRACT.</p>
        <div className="flex gap-3 min-h-0">
          <div className="flex-1 min-w-0 space-y-3">
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Label</Label>
              <Input data-testid="measure-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Profit Margin %" className="mt-1 bg-[color:var(--surface)]" />
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Expression — type [ to reference measures or columns</Label>
              <div className="mt-1">
                <ExpressionEditor value={expr} onChange={setExpr} mode="measure" suggestions={suggestions} apiRef={edApi}
                  testid="measure-expression" onStatusChange={setExprStatus}
                  placeholder={"([total_sales] - [total_cost]) / [total_sales] * 100"}
                  validate={(v) => api.post(`/datasets/${datasetId}/model/validate-measure`, { expression: v }).then((r) => r.data)} />
              </div>
            </div>
            <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Format</Label>
                <Select value={fmt.type} onValueChange={(v) => setFmt((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="measure-format-type" className="mt-1 h-8 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{["number", "currency", "percent"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Decimals</Label>
                <Input type="number" min={0} max={6} data-testid="measure-format-decimals" value={fmt.decimals}
                  onChange={(e) => setFmt((f) => ({ ...f, decimals: Math.max(0, Math.min(6, Number(e.target.value) || 0)) }))} className="mt-1 h-8 bg-[color:var(--surface)]" />
              </div>
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Prefix / Suffix</Label>
                <div className="flex gap-1.5 mt-1">
                  <Input data-testid="measure-format-prefix" value={fmt.prefix} onChange={(e) => setFmt((f) => ({ ...f, prefix: e.target.value }))} placeholder="₹" className="h-8 bg-[color:var(--surface)]" />
                  <Input data-testid="measure-format-suffix" value={fmt.suffix} onChange={(e) => setFmt((f) => ({ ...f, suffix: e.target.value }))} placeholder="pts" className="h-8 bg-[color:var(--surface)]" />
                </div>
              </div>
              <div className="flex items-end justify-between pb-1">
                <Label className="text-[11px] text-[color:var(--ink-3)]">Compact (1.2K / 3.4M)</Label>
                <Switch data-testid="measure-format-compact" checked={!!fmt.compact} onCheckedChange={(v) => setFmt((f) => ({ ...f, compact: v }))} />
              </div>
            </div>
          </div>
          <FunctionLibraryPanel mode="measure" onInsert={(t) => edApi.current?.insert(t)} />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="measure-save" disabled={busy || !label.trim() || !expr.trim() || exprStatus?.state === "error" || exprStatus?.state === "checking"} onClick={save} className="bg-[color:var(--brand-ocean)] text-white">{exprStatus?.state === "checking" ? "Validating…" : "Save measure"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
